import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminUserDTO } from "@shaarp/common";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast, useToastError } from "../ui/use-toast";

export function DialogAdminUserAdvisor({
  user,
  open,
  onClose,
  onSuccess,
}: {
  user: AdminUserDTO;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { toast } = useToast();
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  const [monthlyDiscount, setMonthlyDiscount] = useState("");
  const [annualDiscount, setAnnualDiscount] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const initializeForm = () => {
    if (user.advisorLicence) {
      setMonthlyDiscount(user.advisorMonthlyDiscount || "");
      setAnnualDiscount(user.advisorAnnualDiscount || "");
    } else {
      setMonthlyDiscount("");
      setAnnualDiscount("");
    }
  };

  useEffect(() => {
    if (open) {
      initializeForm();
    }
  }, [
    open,
    user.advisorLicence,
    user.advisorMonthlyDiscount,
    user.advisorAnnualDiscount,
  ]);

  const handleSubmit = async () => {
    if (monthlyDiscount === "" || annualDiscount === "") {
      return;
    }

    setIsLoading(true);
    try {
      await api.post(`/admin/user/${user.id}/advisor`, {
        monthlyDiscount: monthlyDiscount || null,
        annualDiscount: annualDiscount || null,
      });

      const isEditing = user.advisorLicence;
      toast({
        variant: "default",
        title: isEditing
          ? t("admin.dataTableAdminUsers.toast.advisorUpdated")
          : t("admin.dataTableAdminUsers.toast.advisorAdded"),
        description: isEditing
          ? t("admin.dataTableAdminUsers.toast.advisorUpdatedDescription")
          : t("admin.dataTableAdminUsers.toast.advisorAddedDescription"),
      });
      onClose();
      onSuccess?.();
    } catch (e) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {user.advisorLicence
              ? t("admin.dataTableAdminUsers.dialog.editAdvisor.title")
              : t("admin.dataTableAdminUsers.dialog.addAdvisor.title")}
          </DialogTitle>
          <DialogDescription>
            {user.advisorLicence
              ? t("admin.dataTableAdminUsers.dialog.editAdvisor.description", {
                  name: user.fullName,
                })
              : t("admin.dataTableAdminUsers.dialog.addAdvisor.description", {
                  name: user.fullName,
                })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex gap-4 items-center">
            <Label
              htmlFor="monthlyDiscount"
              className="text-left whitespace-nowrap"
            >
              {t("admin.dataTableAdminUsers.dialog.addAdvisor.monthlyDiscount")}
            </Label>
            <Input
              id="monthlyDiscount"
              type="number"
              min="0"
              max="100"
              placeholder="0"
              value={monthlyDiscount}
              onChange={(e) => setMonthlyDiscount(e.target.value)}
              className="w-20"
            />
          </div>
          <div className="flex gap-4 items-center">
            <Label
              htmlFor="annualDiscount"
              className="text-left whitespace-nowrap"
            >
              {t("admin.dataTableAdminUsers.dialog.addAdvisor.annualDiscount")}
            </Label>
            <Input
              id="annualDiscount"
              type="number"
              min="0"
              max="100"
              placeholder="0"
              value={annualDiscount}
              onChange={(e) => setAnnualDiscount(e.target.value)}
              className="w-20"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("admin.dataTableAdminUsers.dialog.addAdvisor.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isLoading || monthlyDiscount === "" || annualDiscount === ""
            }
          >
            {isLoading ? (
              <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
            ) : user.advisorLicence ? (
              t("admin.dataTableAdminUsers.dialog.editAdvisor.confirm")
            ) : (
              t("admin.dataTableAdminUsers.dialog.addAdvisor.confirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
